import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { replacePathParams } from '@/common/utils/utils';
import TablePlay from '@/features/poker-table/TablePlay';
import ReplayActionsButtons from '@/pages/replay/components/ReplayActionsButtons/ReplayActionsButtons';
import ReplayHandActionButtons from '@/pages/replay/components/ReplayHandActionButtons/ReplayHandActionButtons';
import ROUTES from '@/routers/Routes';
import useGetHandHistory from '@/services/table/queries/useGetHandHistory';
import useGetTableHandHistory from '@/services/table/queries/useGetTableHandHistory';
import { PlayerSeatDto, TableState } from '@/services/table/tableState';
import { Card } from '@/services/table/tableTypes';

import styles from './Replay.module.css';

const Replay: React.FC = () => {
  const { tableId, handId } = useParams<{ tableId: string; handId: string }>();
  const navigate = useNavigate();

  const { data: handHistoryData, isLoading, error } = useGetHandHistory(tableId ?? '', handId ?? '');
  const { data: handHistory } = useGetTableHandHistory(tableId ?? '');

  const gamesCount = handHistory?.length;
  const currentGamePosition = (handHistory?.findIndex((hand) => hand?.handId === handId) ?? -1) + 1;
  const currentGame = handHistory?.find((hand) => hand?.handId === handId);

  const [currentState, setCurrentState] = useState<TableState | null>(null);
  const [intervalId, setIntervalId] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const playerMappings = useMemo(() => {
    if (handHistoryData?.handHistory) {
      return handHistoryData.handHistory.mappings;
    }
    return {};
  }, [handHistoryData]);

  const updateStateWithPlayerCards = (state: TableState): TableState => {
    const updatedState: TableState = {
      ...state,
      playerSeat: undefined,
    };

    if (updatedState.seats && updatedState.handState?.state && handHistoryData?.handHistory) {
      const playerSeatIndex = updatedState.seats.findIndex(
        (seat) => seat && seat.userId === handHistoryData.handHistory.userId,
      );
      if (playerSeatIndex !== -1 && updatedState.seats[playerSeatIndex]?.cardIndexes) {
        const playerSeat = updatedState.seats[playerSeatIndex] as PlayerSeatDto;

        const playerCards = playerSeat.cardIndexes.map((index: number) => playerMappings[index] || '?');

        if (typeof playerSeat.playerIndex === 'number') {
          const updatedHoleCards: Card[] = playerCards.map((card: string) => ({
            rank: card[0],
            suit: card[1],
          }));

          // Update handState.state.hole_cards
          if (updatedState.handState.state.hole_cards) {
            updatedState.handState.state.hole_cards[playerSeat.playerIndex] = updatedHoleCards;
          }

          // Update seat.holeCards
          updatedState.seats[playerSeatIndex] = {
            ...playerSeat,
            holeCards: updatedHoleCards,
          };

          playerSeat.holeCards = updatedHoleCards;

          // Set seatPlayer to the player's seat
          updatedState.playerSeat = playerSeat;
        }
      }
    }
    return updatedState;
  };

  useEffect(() => {
    if (handHistoryData?.states && handHistoryData.states.length > 0) {
      const updatedState = updateStateWithPlayerCards(handHistoryData.states[currentIndex]);
      setCurrentState(updatedState);
    }
  }, [handHistoryData, currentIndex, playerMappings]);

  const onClickPlay = () => {
    if (intervalId === null && handHistoryData) {
      const id = window.setInterval(() => {
        onClickFastForward();
      }, 1000);
      setIntervalId(id);
    }
  };

  const onClickPause = () => {
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  const onClickFastForward = () => {
    if (handHistoryData && currentIndex < handHistoryData.states.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    } else {
      onClickPause();
    }
  };

  const onClickSkipForward = () => {
    if (handHistoryData) {
      setCurrentIndex(handHistoryData.states.length - 1);
    }
  };

  const onClickSkipBackward = () => {
    setCurrentIndex(0);
  };

  const onClickRewind = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const navigateToGame = (handIndex: number) => {
    if (handHistory) {
      const handId = handHistory[handIndex]?.handId || '';
      navigate(replacePathParams(ROUTES.REPLAY, { tableId: tableId || '', handId }));
    }
  };

  const onClickSkipBackwardGame = () => {
    navigateToGame(0);
  };

  const onClickSkipForwardGame = () => {
    if (handHistory) {
      navigateToGame(handHistory.length - 1);
    }
  };

  const onClickNextGame = () => {
    navigateToGame(currentGamePosition);
  };

  const onClickPreviousGame = () => {
    if (currentGamePosition > 1) {
      navigateToGame(currentGamePosition - 2);
    }
  };
  if (!tableId || !handId) {
    return <div>No table or hand id provided</div>;
  }

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading hand history</div>;

  return (
    <div className="relative">
      <div className="absolute left-5 top-4 flex flex-col">
        <span className="text-sm font-bold text-ph-white-50">Current Replay</span>
        <span className="text-sm font-black text-white">#{currentGame?.handId}</span>
      </div>
      <div className={twMerge(styles.container, 'min-h-screen items-center justify-center gap-8 p-12')}>
        <div
          className={twMerge(styles.tableContainer, 'mb-20 mt-8 flex min-h-0 flex-grow items-center justify-center')}
        >
          {currentState && <TablePlay tableState={{ ...currentState, isConnected: true }} />}
        </div>

        <div className="flex flex-col items-center justify-center gap-2">
          <span className="text-xs font-bold text-ph-purple">
            Game {currentGamePosition} of {gamesCount}
          </span>
          <ReplayHandActionButtons
            onClickPreviousGame={onClickPreviousGame}
            onClickNextGame={onClickNextGame}
            onClickSkipBackwardGame={onClickSkipBackwardGame}
            onClickSkipForwardGame={onClickSkipForwardGame}
            currentGamePosition={currentGamePosition}
            gamesCount={gamesCount}
          />
        </div>
        <div className="flex justify-center">
          <ReplayActionsButtons
            onClickPlay={onClickPlay}
            onClickPause={onClickPause}
            onClickSkipForward={onClickSkipForward}
            onClickSkipBackward={onClickSkipBackward}
            onClickFastForward={onClickFastForward}
            onClickRewind={onClickRewind}
          />
        </div>
      </div>
    </div>
  );
};

export default Replay;
